export const SNAPSHOT_FILTER_ALL = 'all';
export const SNAPSHOT_FILTER_VALID = 'valid';
export const SNAPSHOT_FILTER_FLAGGED = 'flagged';
export const SNAPSHOT_FILTER_INVALID = 'invalid';

export const SNAPSHOT_ROW_STATUSES = ['valid', 'flagged', 'invalid'];

/**
 * Exclusive row status for snapshot chips, filters, and counts.
 *
 * @param {object} beer - Snapshot beer
 * @returns {'valid'|'flagged'|'invalid'}
 */
export function snapshotBeerStatus(beer) {
  const codes = Array.isArray(beer?.issues)
    ? beer.issues.map((issue) => issue.code)
    : [];
  if (codes.includes('empty_name')) {
    return 'invalid';
  }
  if (codes.includes('missing_style') || codes.includes('malformed_abv')) {
    return 'flagged';
  }
  return 'valid';
}

/**
 * Chip color for a snapshot row status.
 *
 * @param {'valid'|'flagged'|'invalid'} status - Exclusive row status
 * @returns {'success'|'warning'|'error'}
 */
export function snapshotStatusChipColor(status) {
  if (status === 'valid') {
    return 'success';
  }
  if (status === 'flagged') {
    return 'warning';
  }
  return 'error';
}

/**
 * Chip label for a snapshot row status.
 *
 * @param {'valid'|'flagged'|'invalid'} status - Exclusive row status
 * @returns {string}
 */
export function snapshotStatusChipLabel(status) {
  if (status === 'valid') {
    return 'Valid';
  }
  if (status === 'flagged') {
    return 'Flagged';
  }
  return 'Invalid';
}

/**
 * Filters snapshot beers by exclusive status.
 *
 * @param {Array<object>} beers - Snapshot beers
 * @param {string} filter - All, valid, flagged, or invalid
 * @returns {Array<object>}
 */
export function filterSnapshotBeers(beers, filter = SNAPSHOT_FILTER_ALL) {
  const rows = Array.isArray(beers) ? beers : [];
  if (!filter || filter === SNAPSHOT_FILTER_ALL) {
    return rows;
  }
  return rows.filter((beer) => snapshotBeerStatus(beer) === filter);
}

/**
 * Formats a normalized ABV for display.
 *
 * @param {number|null|undefined} abv - Normalized ABV
 * @returns {string|null}
 */
export function formatNormalizedAbv(abv) {
  if (typeof abv !== 'number' || !Number.isFinite(abv)) {
    return null;
  }
  return abv.toFixed(1);
}

/**
 * Returns a Raw: caption when trimmed raw differs from the displayed normalized value.
 *
 * @param {string|undefined} rawValue - Original field string
 * @param {string|null} displayedNormalized - Display text for the normalized value
 * @returns {string|null}
 */
export function rawValueCaption(rawValue, displayedNormalized) {
  const trimmedRaw = typeof rawValue === 'string' ? rawValue.trim() : '';
  if (!trimmedRaw) {
    return null;
  }
  if (trimmedRaw === (displayedNormalized ?? '')) {
    return null;
  }
  return `Raw: ${trimmedRaw}`;
}

export const ISSUE_DISPLAY_MESSAGES = {
  empty_name: 'Name is required',
  missing_style: 'Style is required',
  malformed_abv: 'ABV could not be parsed',
  missing_abv: 'ABV is missing',
};

/**
 * Joins human-readable issue messages for a snapshot beer.
 *
 * @param {object} beer - Snapshot beer
 * @returns {string}
 */
export function formatSnapshotBeerIssues(beer) {
  const issues = Array.isArray(beer?.issues) ? beer.issues : [];
  const messages = issues.map((issue) => (
    ISSUE_DISPLAY_MESSAGES[issue.code] || issue.message || issue.code
  ));
  return messages.filter(Boolean).join(', ');
}
